import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { Box, Drawer, IconButton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { BrowserView, MobileView } from 'react-device-detect';
import MenuList from './MenuList';
import SidebarFooter from './SidebarFooter';
import LogoSection from '../LogoSection';
import { drawerWidth, SIDEBAR } from '../../../store/constant';
import { SET_MENU, SET_SIDEBAR_WIDTH } from '../../../store/actions';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons';

const INTERACTIVE = 'a, button, input, textarea, select, [role="button"], [role="separator"], [role="menuitem"], .MuiListItemButton-root';

const Sidebar = ({ drawerOpen, drawerToggle, onResizeChange, userData, window }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));

  const opened = useSelector((state) => state.customization.opened);
  const sidebarWidth = useSelector((state) => state.customization.sidebarWidth);

  const collapsed = matchUpMd && !opened;
  const width = matchUpMd ? (collapsed ? SIDEBAR.collapsedWidth : sidebarWidth) : drawerWidth;

  const [dragging, setDragging] = useState(false);
  const stopDragRef = useRef(null);
  const openedRef = useRef(opened);
  openedRef.current = opened;

  useEffect(() => () => stopDragRef.current && stopDragRef.current(), []);

  useEffect(() => {
    if (onResizeChange) onResizeChange(dragging);
  }, [dragging, onResizeChange]);

  const toggleCollapsed = () => dispatch({ type: SET_MENU, opened: !openedRef.current });

  useEffect(() => {
    if (!matchUpMd) return undefined;
    const onKey = (e) => {
      if (e.key !== '[' || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      dispatch({ type: SET_MENU, opened: !openedRef.current });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [matchUpMd, dispatch]);

  const handlePointerDown = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();

    const startX = event.clientX;
    const startWidth = width;
    let lastX = startX;
    let frame = 0;

    const onMove = (e) => {
      lastX = e.clientX;
      if (!frame) {
        frame = requestAnimationFrame(() => {
          frame = 0;
          dispatch({ type: SET_SIDEBAR_WIDTH, sidebarWidth: startWidth + (lastX - startX) });
        });
      }
    };
    const stop = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', stop);
      document.removeEventListener('pointercancel', stop);
      if (frame) cancelAnimationFrame(frame);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      stopDragRef.current = null;
      setDragging(false);
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', stop);
    document.addEventListener('pointercancel', stop);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    stopDragRef.current = stop;
    setDragging(true);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleCollapsed();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      if (collapsed) dispatch({ type: SET_MENU, opened: true });
      else dispatch({ type: SET_SIDEBAR_WIDTH, sidebarWidth: width + 16 });
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      if (collapsed) return;
      if (width <= SIDEBAR.minWidth) dispatch({ type: SET_MENU, opened: false });
      else dispatch({ type: SET_SIDEBAR_WIDTH, sidebarWidth: width - 16 });
    }
  };

  const handlePaperClick = (event) => {
    if (!collapsed) return;
    if (event.target.closest && event.target.closest(INTERACTIVE)) return;
    dispatch({ type: SET_MENU, opened: true });
  };

  const version = !collapsed && process.env.REACT_APP_VERSION && (
    <Typography sx={{ fontSize: 11, color: 'text.disabled', textAlign: 'center', mt: 1, mb: 2 }}>{process.env.REACT_APP_VERSION}</Typography>
  );

  const pad = collapsed ? '12px' : '16px';

  const brand = matchUpMd && (
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        height: 66,
        pl: pad,
        pr: pad
      }}
    >
      {collapsed ? <LogoSection compact /> : <LogoSection width={96} />}
    </Box>
  );

  const footer = matchUpMd && (
    <Box sx={{ flexShrink: 0, pt: 1.5, pb: 2, pl: pad, pr: pad, borderTop: 1, borderColor: 'divider' }}>
      <SidebarFooter userData={userData} collapsed={collapsed} />
    </Box>
  );

  const drawer = (
    <>
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <Box sx={{ display: 'flex', p: 2, mx: 'auto' }}>
          <LogoSection />
        </Box>
      </Box>
      {brand}
      <BrowserView style={{ flex: 1, minHeight: 0 }}>
        <PerfectScrollbar
          component="div"
          style={{
            height: !matchUpMd ? 'calc(100vh - 56px)' : '100%',
            paddingLeft: pad,
            paddingRight: collapsed ? '2px' : '6px',
            marginRight: matchUpMd ? '10px' : 0
          }}
        >
          <MenuList />
          {version}
        </PerfectScrollbar>
      </BrowserView>
      <MobileView style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Box sx={{ px: 2 }}>
          <MenuList />
          {version}
        </Box>
      </MobileView>
      {footer}

      {matchUpMd && (
        <Box
          role="separator"
          aria-orientation="vertical"
          aria-label="Meniu plotis"
          aria-valuemin={SIDEBAR.collapsedWidth}
          aria-valuemax={SIDEBAR.maxWidth}
          aria-valuenow={width}
          tabIndex={0}
          title="Tempkite, kad pakeistumėte plotį"
          onPointerDown={handlePointerDown}
          onDoubleClick={toggleCollapsed}
          onKeyDown={handleKeyDown}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: 8,
            zIndex: 2,
            cursor: 'col-resize',
            touchAction: 'none',
            outline: 'none',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              right: 0,
              width: 2,
              backgroundColor: theme.palette.primary.main,
              opacity: dragging ? 1 : 0,
              transition: 'opacity .15s ease'
            },
            '&:hover::after, &:focus-visible::after': { opacity: 1 }
          }}
        />
      )}
    </>
  );

  const container = window !== undefined ? () => window.document.body : undefined;
  const widthTransition = dragging ? 'none' : theme.transitions.create('width', { duration: 200 });
  const toggleLabel = collapsed ? 'Išskleisti meniu' : 'Suskleisti meniu';

  return (
    <Box
      component="nav"
      aria-label="Pagrindinis meniu"
      sx={{ flexShrink: { md: 0 }, width: matchUpMd ? width : 'auto', transition: widthTransition }}
    >
      <Drawer
        container={container}
        variant={matchUpMd ? 'permanent' : 'temporary'}
        anchor="left"
        open={matchUpMd ? true : drawerOpen}
        onClose={drawerToggle}
        PaperProps={{ onClick: handlePaperClick }}
        sx={{
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            background: theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRight: 'none',
            transition: widthTransition,
            cursor: collapsed ? 'e-resize' : 'default',
            '& a, & button, & .MuiListItemButton-root': { cursor: 'pointer' }
          }
        }}
        ModalProps={{ keepMounted: true }}
        color="inherit"
      >
        {drawer}
      </Drawer>

      {matchUpMd && (
        <Tooltip title={`${toggleLabel} ([)`} placement="right">
          <IconButton
            onClick={toggleCollapsed}
            aria-label={toggleLabel}
            aria-expanded={!collapsed}
            sx={{
              position: 'fixed',
              top: 20,
              left: width - 13, // on the sidebar edge
              zIndex: theme.zIndex.drawer + 2,
              width: 26,
              height: 26,
              p: 0,
              borderRadius: '50%',
              bgcolor: 'background.paper',
              color: 'text.secondary',
              border: 1,
              borderColor: 'divider',
              boxShadow: theme.palette.custom.shadows.sm,
              transition: dragging ? 'none' : theme.transitions.create(['left', 'background-color', 'color'], { duration: 200 }),
              '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText', borderColor: 'primary.main' }
            }}
          >
            {collapsed ? <IconChevronRight stroke={2} size={14} /> : <IconChevronLeft stroke={2} size={14} />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

Sidebar.propTypes = {
  drawerOpen: PropTypes.bool,
  drawerToggle: PropTypes.func,
  onResizeChange: PropTypes.func,
  userData: PropTypes.object,
  window: PropTypes.object
};

export default Sidebar;